import React, {useCallback, useContext, useState} from 'react';

import debounce from 'lodash.debounce';
import {FormInput} from 'src/components/input';
import {MessageContext} from 'src/contexts/message-context';
import {addConferenceUser} from 'src/openapi/admin/apis/ConferenceApi';
import {getUsersByEmails} from 'src/openapi/admin/apis/GetUserByEmailApi';
import {useAppDispatch} from 'src/store/hooks';

import Button from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';


type Props = {
    accountId: number,
    onClose: () => void,
    onAdd: () => void,
};

export const AddAttendeeModal = ({accountId, onClose, onAdd}: Props) => {
    const apiDispatch = useAppDispatch();
    const {setSuccessMessage} = useContext(MessageContext);

    const [addBusy, setAddBusy] = useState(false);
    const [checkBusy, setCheckBusy] = useState(false);

    const [email, setEmail] = useState('');
    const [serverError, setServerError] = useState('');
    const [emailError, setEmailError] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const changeEmail = useCallback(debounce(async (newEmail: string) => {
        setEmail(newEmail);

        if (!newEmail) {
            setEmailError('');
            return;
        }

        setCheckBusy(true);
        const existedUsersResponse = await apiDispatch(getUsersByEmails({
            requestBody: [newEmail],
        }));
        setCheckBusy(false);

        if (existedUsersResponse.payload.success) {
            const existedUsers = existedUsersResponse.payload.data;
            if (!existedUsers.length) {
                setEmailError('User not exists in the system and can not be added to the conference.');
            } else {
                setEmailError('');
            }

            setServerError('');
        } else {
            setServerError(existedUsersResponse.payload.data.message || 'Service is temporarily unavailable');
        }
    }, 1000), []);

    const addAttendee = useCallback(async () => {
        setAddBusy(true);
        const response = await apiDispatch(addConferenceUser({
            accountId,
            addConferenceUserRequest: {emails: [email]},
        }));
        setAddBusy(false);

        if (response.payload.success) {
            onClose();
            onAdd();
            setSuccessMessage('Attendees successfully added to the conference');
        } else {
            setServerError(response.payload.data.message || 'Service is temporarily unavailable');
        }
    }, [accountId, email, apiDispatch, onAdd, onClose, setSuccessMessage]);

    return (
        <>
            <DialogHeader onClose={onClose}>
                Add Attendee
            </DialogHeader>
            <DialogMain style={{padding: 24}}>
                {serverError ? <Message color="error">{serverError}</Message> : null}
                <FormInput
                    width={300}
                    busy={checkBusy}
                    label="Email"
                    placeholder="Enter email address"
                    value={email}
                    onChange={changeEmail}
                    valid={email ? !emailError : undefined}
                    errorDescription={emailError}
                />
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button
                        variant="Type.PRIMARY"
                        disabled={!!emailError || !email || checkBusy}
                        busy={addBusy}
                        onClick={addAttendee}>
                        Add
                    </Button>
                    <Button variant="Type.SECONDARY" onClick={onClose}>
                        Cancel
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
